'use client';

import { Button } from '@/components/button';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import { RichText as RichTextComponent } from '@/components/richText/RichText';
import { SwdCircularProgress } from '@/components/swdCircularProgress/SwdCircularProgress';
import { installGigyaFloatingLabelTextStacks } from '@/helper/gigyaFloatingLabelShadow';
import { getSelfServiceLoginLink } from '@/helper/selfServicePath';
import type { GlobalLoginFormStoryblok } from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import classes from './LoginForm.module.scss';
import { isLoginScreenOidcOnly, oidcModeMatchesScreen, textResolver } from './LoginFormUtils';
import { ScreensetScreens } from './types';

const SCREENSET = 'Default-RegistrationLogin';

const LOGIN_DEBUG_PREFIX = '[LoginForm Gigya]';

type LoginFormProps = {
  blok: GlobalLoginFormStoryblok;
  initialScreen?: ScreensetScreens;
};

const LoginForm: React.FC<LoginFormProps> = ({ blok, initialScreen }) => {
  const [loadingScreenset, setLoadingScreenset] = useState<boolean>(true);
  const [showFallbackError, setShowFallbackError] = useState(false);

  const [currentScreen, setCurrentScreen] = useState<string>(initialScreen ?? ScreensetScreens.LOGIN_SCREEN);

  const screensetShownRef = useRef(false);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const afterScreenLoadHandler = (e: Event) => {
    const detail = (e as CustomEvent<{ currentScreen?: string }>).detail;
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    setShowFallbackError(false);
    setLoadingScreenset(false);
    if (detail?.currentScreen) {
      setCurrentScreen(detail.currentScreen);
      queueMicrotask(() => installGigyaFloatingLabelTextStacks());
    }
  };

  useEffect(() => {
    fallbackTimerRef.current = setTimeout(() => {
      setShowFallbackError(true);
      fallbackTimerRef.current = null;
    }, 10000);

    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const targetScreen = initialScreen ?? ScreensetScreens.LOGIN_SCREEN;

    const showScreenset = () => {
      if (screensetShownRef.current) {
        console.debug(`${LOGIN_DEBUG_PREFIX} showScreenset aborted: already shown`);
        return;
      }
      if (cancelled) {
        console.debug(`${LOGIN_DEBUG_PREFIX} showScreenset aborted: effect cancelled`);
        return;
      }
      if (typeof window.gigya === 'undefined') {
        console.debug(`${LOGIN_DEBUG_PREFIX} showScreenset aborted: window.gigya missing`);
        return;
      }
      screensetShownRef.current = true;

      try {
        window.gigya.accounts.showScreenSet({
          screenSet: SCREENSET,
          containerID: 'screenset',
          startScreen: targetScreen,
          authFlow: 'redirect',
        });
      } catch (error) {
        console.error(`${LOGIN_DEBUG_PREFIX} showScreenSet threw`, error);
        screensetShownRef.current = false;
      }
    };

    const tryShowFromOidc = (params: { mode?: string }) => {
      if (cancelled || screensetShownRef.current) {
        console.debug(`${LOGIN_DEBUG_PREFIX} OIDC ignored`, { cancelled, alreadyShown: screensetShownRef.current });
        return;
      }
      if (!oidcModeMatchesScreen(params.mode, targetScreen)) {
        console.debug(`${LOGIN_DEBUG_PREFIX} OIDC ignored: mode mismatch`, {
          mode: params.mode,
          targetScreen,
        });
        return;
      }
      showScreenset();
    };

    const onGigyaOidc = (e: Event) => {
      const params = (e as CustomEvent<{ mode?: string }>).detail ?? {};
      delete window.__swdGigyaOidcPending;
      tryShowFromOidc(params);
    };

    /** register, forgot password, reset: displayable without oidc. pure login: only OIDC. */
    const onGigyaServiceReady = () => {
      if (isLoginScreenOidcOnly(targetScreen)) {
        return;
      }
      window.setTimeout(() => {
        if (cancelled || screensetShownRef.current) {
          return;
        }
        if (typeof window.gigya === 'undefined') {
          return;
        }
        console.debug(`${LOGIN_DEBUG_PREFIX} gigya-service-ready (deferred) -> showScreenset, non-login screen`);
        showScreenset();
      }, 0);
    };

    window.addEventListener('gigya-oidc', onGigyaOidc);
    window.addEventListener('gigya-service-ready', onGigyaServiceReady);

    const pending = window.__swdGigyaOidcPending;
    if (pending !== undefined) {
      delete window.__swdGigyaOidcPending;
      console.debug(`${LOGIN_DEBUG_PREFIX} draining pending OIDC`, pending);
      tryShowFromOidc(pending);
    }

    if (!isLoginScreenOidcOnly(targetScreen) && typeof window.gigya !== 'undefined' && window.gigya.accounts) {
      onGigyaServiceReady();
    }

    window.document.addEventListener('gigya-screenset-onafterscreenload', afterScreenLoadHandler);

    return () => {
      cancelled = true;
      window.removeEventListener('gigya-oidc', onGigyaOidc);
      window.removeEventListener('gigya-service-ready', onGigyaServiceReady);
      window.document.removeEventListener('gigya-screenset-onafterscreenload', afterScreenLoadHandler);
    };
  }, [initialScreen]);

  return (
    <div className={classNames(classes.loginForm)} {...storyblokEditable(blok)}>
      <div className={classNames(classes.headline)}>
        <h1 className={classes.caption}>{textResolver(currentScreen, blok)?.headline_small}</h1>
        <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H5} className={classes.prominent}>
          {textResolver(currentScreen, blok)?.headline_big}
        </HeadlineField>
      </div>

      <div className={classes.text}>
        <RichTextComponent bulletPointsListStyle="success" text={textResolver(currentScreen, blok)?.text} />
      </div>
      <div className={classNames(classes.screenset)}>
        {loadingScreenset && !showFallbackError && <SwdCircularProgress />}
        <div id="screenset"></div>
        <div id="screenset_fallback">
          {showFallbackError && (
            <>
              <p>
                <strong>Hinweis</strong>
              </p>
              <p>Leider kam es zu einem technischen Fehler bei der Anmeldung. Bitte versuchen Sie es erneut.</p>
              <Button
                variant="ctaFull"
                href={getSelfServiceLoginLink()}
                label="Zurück zum Login"
                className={classes.fallbackButton}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
